import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { searchServices, applyFilters, defaultFilters, getHotServices, type FilterState } from '@/lib/content';
import ServiceCard from '@/components/base/ServiceCard';
import FilterBar from '@/components/base/FilterBar';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';

const PAGE_SIZE = 6;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const sortParam = (searchParams.get('sort') as FilterState['sort']) || 'latest';

  const [keyword, setKeyword] = useState(query);
  const [filters, setFilters] = useState<FilterState>({ ...defaultFilters, sort: sortParam });
  const [page, setPage] = useState(1);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 在线服务平台` : '搜索资源 - 在线服务平台',
    description: query
      ? `在在线平台搜索“${query}”相关的教育、娱乐、购物等在线服务信息，快速查找所需资源。`
      : '在线平台搜索页，支持按关键词模糊匹配查找各类在线服务信息。',
    keywords: query ? `${query},在线搜索,在线资源` : '在线搜索,在线资源,在线查询',
  });

  const results = useMemo(() => {
    const base = query ? searchServices(query) : [];
    return applyFilters(base, filters);
  }, [query, filters]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const recommended = getHotServices(3);

  const runSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setPage(1);
    setSearchParams(q ? { q } : {});
  };

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters({ ...defaultFilters });
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">搜索资源</h1>
        {query ? (
          <p className="mt-3 text-sm md:text-base text-foreground-600">
            关键词 <strong className="text-primary-600">“{query}”</strong> · 共找到{' '}
            <strong className="text-foreground-900">{results.length}</strong> 条结果
          </p>
        ) : (
          <p className="mt-3 text-sm md:text-base text-foreground-600">输入关键词，搜索教育、娱乐、购物等各类在线服务。</p>
        )}
      </header>

      <form onSubmit={runSearch} className="mb-6">
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索课程、活动、优惠、服务…"
              className="w-full pl-12 pr-4 py-3 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
          <button type="submit" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-search-2-line"></i>
            搜索
          </button>
        </div>
      </form>

      {query && (
        <FilterBar filters={filters} onChange={handleFilterChange} onReset={handleReset} resultCount={results.length} />
      )}

      <div className="mt-6">
        {!query ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="请输入关键词开始搜索"
            description="支持按标题、简介、标签、提供方等模糊匹配，试试“数据分析”“音乐节”“家电优惠”等关键词。"
          />
        ) : pageItems.length === 0 ? (
          <div>
            <EmptyState
              icon="ri-file-search-line"
              title="没有找到相关结果"
              description={`没有与“${query}”匹配的内容，换个关键词或重置筛选试试。`}
              actionText="重置筛选"
              onAction={handleReset}
            />
            <div className="mt-6 pt-6 border-t border-background-200">
              <h2 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>不妨看看热门内容
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {recommended.map((item) => (
                  <ServiceCard key={item.id} service={item} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
              {pageItems.map((item) => (
                <ServiceCard key={item.id} service={item} />
              ))}
            </div>
            <Pagination current={safePage} total={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}